// ============================================
// WHATSAPP COUNTRIES - Dial codes + localized names
// ============================================
// Self-contained module: owns the ISO2 -> dial code dataset, the WhatsApp
// specific overrides, the priority list and the flag helper. Localized
// country names are resolved here via i18n-iso-countries so this module does
// not depend on the i18n store internals.

import countries from 'i18n-iso-countries';
import esCountries from 'i18n-iso-countries/langs/es.json';
import enCountries from 'i18n-iso-countries/langs/en.json';

// registerLocale is idempotent-safe (i18n.ts also registers these)
countries.registerLocale(esCountries);
countries.registerLocale(enCountries);

export interface WhatsappCountry {
    iso: string;
    dial: string;
    value: string; // '+' + dial, ready to concatenate with the phone number
    name: string;  // localized country name
    flag: string;  // emoji flag
    priority: boolean;
}

/**
 * ITU calling codes per ISO 3166-1 alpha-2 country code (digits only, no '+').
 * For countries that share a code (e.g. NANP +1) the standard ITU code is used.
 */
export const DIAL_CODES: Record<string, string> = {
    AF: '93', AL: '355', DZ: '213', AS: '1', AD: '376', AO: '244', AI: '1',
    AG: '1', AR: '54', AM: '374', AW: '297', AU: '61', AT: '43', AZ: '994',
    BS: '1', BH: '973', BD: '880', BB: '1', BY: '375', BE: '32', BZ: '501',
    BJ: '229', BM: '1', BT: '975', BO: '591', BA: '387', BW: '267', BR: '55',
    IO: '246', BN: '673', BG: '359', BF: '226', BI: '257', KH: '855', CM: '237',
    CA: '1', CV: '238', KY: '1', CF: '236', TD: '235', CL: '56', CN: '86',
    CO: '57', KM: '269', CG: '242', CD: '243', CK: '682', CR: '506', CI: '225',
    HR: '385', CU: '53', CW: '599', CY: '357', CZ: '420', DK: '45', DJ: '253',
    DM: '1', DO: '1', EC: '593', EG: '20', SV: '503', GQ: '240', ER: '291',
    EE: '372', SZ: '268', ET: '251', FK: '500', FO: '298', FJ: '679', FI: '358',
    FR: '33', GF: '594', PF: '689', GA: '241', GM: '220', GE: '995', DE: '49',
    GH: '233', GI: '350', GR: '30', GL: '299', GD: '1', GP: '590', GU: '1',
    GT: '502', GG: '44', GN: '224', GW: '245', GY: '592', HT: '509', HN: '504',
    HK: '852', HU: '36', IS: '354', IN: '91', ID: '62', IR: '98', IQ: '964',
    IE: '353', IM: '44', IL: '972', IT: '39', JM: '1', JP: '81', JE: '44',
    JO: '962', KZ: '7', KE: '254', KI: '686', KP: '850', KR: '82', KW: '965',
    KG: '996', LA: '856', LV: '371', LB: '961', LS: '266', LR: '231', LY: '218',
    LI: '423', LT: '370', LU: '352', MO: '853', MG: '261', MW: '265', MY: '60',
    MV: '960', ML: '223', MT: '356', MH: '692', MQ: '596', MR: '222', MU: '230',
    YT: '262', MX: '52', FM: '691', MD: '373', MC: '377', MN: '976', ME: '382',
    MS: '1', MA: '212', MZ: '258', MM: '95', NA: '264', NR: '674', NP: '977',
    NL: '31', NC: '687', NZ: '64', NI: '505', NE: '227', NG: '234', NU: '683',
    MK: '389', MP: '1', NO: '47', OM: '968', PK: '92', PW: '680', PS: '970',
    PA: '507', PG: '675', PY: '595', PE: '51', PH: '63', PL: '48', PT: '351',
    PR: '1', QA: '974', RE: '262', RO: '40', RU: '7', RW: '250', BL: '590',
    KN: '1', LC: '1', MF: '590', PM: '508', VC: '1', WS: '685', SM: '378',
    ST: '239', SA: '966', SN: '221', RS: '381', SC: '248', SL: '232', SG: '65',
    SX: '1', SK: '421', SI: '386', SB: '677', SO: '252', ZA: '27', SS: '211',
    ES: '34', LK: '94', SD: '249', SR: '597', SE: '46', CH: '41', SY: '963',
    TW: '886', TJ: '992', TZ: '255', TH: '66', TL: '670', TG: '228', TK: '690',
    TO: '676', TT: '1', TN: '216', TR: '90', TM: '993', TC: '1', TV: '688',
    UG: '256', UA: '380', AE: '971', GB: '44', US: '1', UY: '598', UZ: '998',
    VU: '678', VA: '39', VE: '58', VN: '84', VG: '1', VI: '1', WF: '681',
    YE: '967', ZM: '260', ZW: '263',
};

/**
 * WhatsApp-specific dial code overrides. WhatsApp requires the mobile trunk
 * digit for some countries (Mexico -> 521, Argentina -> 549). Extend as needed.
 */
export const WHATSAPP_DIAL_OVERRIDES: Record<string, string> = {
    MX: '521',
    AR: '549',
};

/** Countries pinned to the top of the list, in this order. */
export const PRIORITY_COUNTRIES: string[] = ['MX', 'CO', 'US'];

/** Convert an ISO 3166-1 alpha-2 code into its emoji flag. */
export function isoToFlag(iso: string): string {
    if (!iso || iso.length !== 2) return '';
    const codePoints = iso
        .toUpperCase()
        .split('')
        .map(char => 0x1f1e6 + char.charCodeAt(0) - 65);
    return String.fromCodePoint(...codePoints);
}

/**
 * Build the full list of WhatsApp countries with localized names.
 * Priority countries come first (in PRIORITY_COUNTRIES order), then the rest
 * sorted alphabetically by localized name.
 */
export function getWhatsappCountries(lang: string): WhatsappCountry[] {
    const names = countries.getNames(lang, { select: 'official' });

    const list: WhatsappCountry[] = Object.entries(names)
        .map(([iso, name]) => {
            const dial = WHATSAPP_DIAL_OVERRIDES[iso] ?? DIAL_CODES[iso];
            if (!dial) return null;
            return {
                iso,
                dial,
                value: '+' + dial,
                name: name as string,
                flag: isoToFlag(iso),
                priority: PRIORITY_COUNTRIES.includes(iso),
            };
        })
        .filter((c): c is WhatsappCountry => c !== null);

    return list.sort((a, b) => {
        const ai = PRIORITY_COUNTRIES.indexOf(a.iso);
        const bi = PRIORITY_COUNTRIES.indexOf(b.iso);
        if (ai !== -1 || bi !== -1) {
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        }
        return a.name.localeCompare(b.name, lang);
    });
}
